import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Tracks the "current visitor" across admin pages so the admin only has to
 * enter an email once per donor/collector trip — even when the visitor both
 * drops off and picks up books in the same visit. Persisted to localStorage
 * so a page refresh mid-batch doesn't lose context.
 */

const STORAGE_KEY = "pp.admin.visitor";

interface AdminSession {
  visitorEmail: string | null;
  setVisitor: (email: string) => void;
  clear: () => void;
}

const AdminSessionCtx = createContext<AdminSession | null>(null);

function readStored(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

export function AdminSessionProvider({ children }: { children: ReactNode }) {
  const [visitorEmail, setVisitorEmail] = useState<string | null>(() => readStored());

  useEffect(() => {
    try {
      if (visitorEmail) window.localStorage.setItem(STORAGE_KEY, visitorEmail);
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // localStorage may be unavailable in private mode — silent ignore.
    }
  }, [visitorEmail]);

  const setVisitor = useCallback((email: string) => {
    setVisitorEmail(email.trim().toLowerCase());
  }, []);
  const clear = useCallback(() => {
    setVisitorEmail(null);
  }, []);

  const value = useMemo<AdminSession>(
    () => ({ visitorEmail, setVisitor, clear }),
    [visitorEmail, setVisitor, clear],
  );

  return <AdminSessionCtx.Provider value={value}>{children}</AdminSessionCtx.Provider>;
}

export function useAdminSession(): AdminSession {
  const ctx = useContext(AdminSessionCtx);
  if (!ctx) {
    throw new Error("useAdminSession must be used inside AdminSessionProvider");
  }
  return ctx;
}
